<template>
  <el-container class="container">
    <el-main ref="main">
      <div ref="viewport" class="viewport">
        <!-- tabindex allows for div to be focused -->
        <div ref="display" class="display" tabindex="0" id="vnc_viewport"/>
      </div>
    </el-main>
    <div class="popContainer" v-if="connectError">
      <div class="connectAgain" @click="startGuacamole">
        连接已断开，点击重新连接
      </div>
    </div>
  </el-container>

</template>

<script>
import Guacamole from 'guacamole-common-js';
import states from '@/common/constants/guacamole-states';
import clipboard from '@/common/libs/clipboard';
import { mapGetters, mapState } from 'vuex';

function serialize(obj) {
  const str = [];
  for (const p in obj) if (obj[p]) {
    str.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
  }
  return str.join('&');
}
export default {
  name: 'GuacClient',
  props: {
    getWindowsResolution: Function,
  },
  data() {
    return {
      display: null, // 展示对象
      connected: false,   // 连接状态
      startingVnc: true, // 允许连接VNC
      connectTimer: null, // 重新连接timer
      client: null, // 远程连接客户端
      keyboard: null, // 远程连接键盘
      mouse: null, // 远程连接鼠标
      connectionState: states.IDLE, // 当前连接状态
      errorMessage: '', // 错误信息
      arguments: {},
      query: {
        screen_width: 1024, // 屏幕宽度
        screen_height: 768, // 屏幕高度
        screen_dpi: 256, // 屏幕DPI
        projectId: 0, // 项目ID
        instanceId: '',
      },
      connectError: false, // 连接失败，遮罩层
    };
  },
  computed: {
    ...mapState(['app']),
    ...mapGetters('app', ['getCurrentProject']),
    wsUrl() {
      const loc = window.location;
      const { search } = window.location;
      const newSearch = search.slice(1);
      const params = {};
      const arr = newSearch.split('&');
      arr.forEach((item) => {
        params[item.split('=')[0]] = item.split('=')[1];
      });
      let newUri;
      if (loc.protocol === 'https:') {
        newUri = 'wss:';
      } else {
        newUri = 'ws:';
      }
      // new_uri += "//127.0.0.1:9528"
      newUri += `//${loc.host}`;
      newUri += '/ws';
      // newUri += (params.instanceId || '');
      return `${newUri}`;
    },
  },
  watch: {
    connectionState(state) {
      this.$notify({
        title: state,
        type: 'success',
      });
    },
  },
  async created() {
    const { instanceId } = this.$route.query;
    this.query.instanceId = instanceId;
    await this.doGuacdConnect();
  },
  methods: {
    /**
     * 远程连接
     */
    async doGuacdConnect() {
      await this.getInitSize();
      await this.setScreenSize();
      await this.startGuacamole();
    },
    /**
     * 获取初始化的信息
     * @param {*} cmd 发送内容
     */
    getInitSize() {
      const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
      const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
      this.query.screen_width = width;
      this.query.screen_height = height - 60;
    },
    /**
     * 发送信息到远程服务器
     * @param {*} cmd 发送内容
     */
    send(cmd) {
      if (!this.client) {
        return;
      }
      for (const c of cmd.data) {
        this.client.sendKeyEvent(1, c.charCodeAt(0));
      }
    },

    /**
     * 复制
     * @param {*} cmd 复制内容
     */
    copy(cmd) {
      if (!this.client) {
        return;
      }
      clipboard.cache = {
        type: 'text/plain',
        data: cmd,
      };
      clipboard.setRemoteClipboard(this.client);
    },
    /**
     * 处理鼠标的状态
     * @param {*} mouseState 鼠标状态
     */
    handleMouseState(mouseState) {
      const scaledMouseState = Object.assign({}, mouseState, {
        x: mouseState.x / this.display.getScale(),
        y: mouseState.y / this.display.getScale(),
      });
      this.client.sendMouseState(scaledMouseState);
    },
    /**
     * 设置屏幕大小
     */
    setScreenSize() {
      const elm = this.$refs.viewport;
      if (!elm || !elm.offsetWidth) {
        // resize is being called on the hidden window
        return;
      }
      const pixelDensity = window.devicePixelRatio || 1;
      const width = elm.clientWidth * pixelDensity;
      const height = elm.clientHeight * pixelDensity;
      const dpi = 256 / pixelDensity * 1.5;
      this.query.screen_height = Math.ceil(height);
      this.query.screen_width = Math.ceil(width);
      this.query.screen_dpi = Math.ceil(dpi);
      this.getWindowsResolution(Math.ceil(dpi));
    },
    /**
     * 重置canvas大小
     */
    resize() {
      const elm = this.$refs.viewport;
      if (!elm || !elm.offsetWidth) {
        // resize is being called on the hidden window
        return;
      }

      const pixelDensity = window.devicePixelRatio || 1;
      const width = elm.clientWidth * pixelDensity;
      const height = elm.clientHeight * pixelDensity;
      if (this.display.getWidth() !== width || this.display.getHeight() !== height) {
        this.client.sendSize(width, height);
      }
      // setting timeout so display has time to get the correct size
      setTimeout(() => {
        const scale = Math.min(
          elm.clientWidth / Math.max(this.display.getWidth(), 1),
          elm.clientHeight / Math.max(this.display.getHeight(), 1),
        );
        this.display.scale(scale);
      }, 100);
    },

    /**
     * 修改分辨率并重启连接
     */
    async windowsResolution(val) {
      this.$emit('changeMessage', '正在调整分辨率...');
      this.query.screen_dpi = val;

      // 先断开当前连接
      if (this.client) {
        this.client.disconnect();
        this.display.scale(0);
        this.uninstallKeyboard();
      }

      // 重置连接状态
      this.connectError = false;
      this.startingVnc = true;

      // 重新计算屏幕尺寸
      // await this.setScreenSize();

      // 重新建立连接
      await this.startGuacamole();
    },

    /**
     * 开始远程连接
     */
    startGuacamole() {
      // GuacadAddr    string `form:"guacad_addr"`
      // AssetProtocol string `form:"asset_protocol"`
      // AssetHost     string `form:"asset_host"`
      // AssetPort     string `form:"asset_port"`
      // AssetUser     string `form:"asset_user"`
      // AssetPassword string `form:"asset_password"`
      // ScreenWidth   int    `form:"screen_width"`
      // ScreenHeight  int    `form:"screen_height"`
      // ScreenDpi     int    `form:"screen_dpi"`

      const tunnel = new Guacamole.WebSocketTunnel(this.wsUrl);


      if (this.client) {
        this.display.scale(0);
        this.uninstallKeyboard();
      }

      this.client = new Guacamole.Client(tunnel);
      clipboard.install(this.client);

      tunnel.onerror = (status) => {
        // eslint-disable-next-line no-console
        console.error(`Tunnel failed ${JSON.stringify(status)}`);
        // this.connectionState = states.TUNNEL_ERROR;
      };

      // tunnel.onstatechange = (state) => {
      //   switch (state) {
      //     // Connection is being established
      //     case Guacamole.Tunnel.State.CONNECTING:
      //       this.connectionState = states.CONNECTING;
      //       break;

      //       // Connection is established / no longer unstable
      //     case Guacamole.Tunnel.State.OPEN:
      //       this.connectionState = states.CONNECTED;
      //       break;

      //       // Connection is established but misbehaving
      //     case Guacamole.Tunnel.State.UNSTABLE:
      //       // TODO
      //       // this.$message.error("不稳定")
      //       break;

      //       // Connection has closed
      //     case Guacamole.Tunnel.State.CLOSED:
      //       this.connectionState = states.DISCONNECTED;
      //       break;
      //   }
      // };

      this.client.onstatechange = (clientState) => {
        switch (clientState) {
          case 0:
            // this.connectionState = states.IDLE;
            break;
          case 1:
            // connecting ignored for some reason?
            break;
          case 2:
            this.$emit('changeMessage', '远程连接中...');
            this.connectionState = states.WAITING;
            break;
          case 3:
            this.connectionState = states.CONNECTED;
            this.connectError = false;
            window.addEventListener('resize', this.resize);
            this.$refs.viewport.addEventListener('mouseenter', this.resize);
            document.getElementById('vnc_viewport').addEventListener('contextmenu', this.handleCanvasCopyData);
            this.$refs.viewport.onkeydown = (event) => {
              if (event.ctrlKey) {
                this.handleCanvasCopyData();
              }
            };
            clipboard.setRemoteClipboard(this.client);

            this.$emit('changeMessage', '远程连接正常！');

            // eslint-disable-next-line no-fallthrough
          case 4:
          case 5:
            // disconnected, disconnecting
            break;
        }
      };

      this.client.onerror = async (error) => {
        this.client.disconnect();
        // eslint-disable-next-line no-console
        // this.$message.error(`Client error ${JSON.stringify(error)}`)
        this.$emit('changeMessage', '远程连接异常！');
        this.connectError = true;
        this.errorMessage = error.message;
        // this.connectionState = states.CLIENT_ERROR;
        if (!this.connectTimer) {
          this.connectTimer = setTimeout(async () => {
            try {
              if (this.startingVnc) {
                this.startingVnc = true;
                // this.doGuacdConnect();
                this.$emit('changeMessage', '远程连接正常！');
                this.connectTimer = null;
              }
            } catch (e) {
              this.connectError = true;
              this.startingVnc = false;
            }
          }, 300);
        }
      };

      this.client.onsync = () => {
      };

      // Test for argument mutability whenever an argument value is received
      this.client.onargv = (stream, mimetype, name) => {
        if (mimetype !== 'text/plain') return;

        const reader = new Guacamole.StringReader(stream);

        // Assemble received data into a single string
        let value = '';
        reader.ontext = (text) => {
          value += text;
        };

        // Test mutability once stream is finished, storing the current value for the argument only if it is mutable
        reader.onend = () => {
          const stream = this.client.createArgumentValueStream('text/plain', name);
          stream.onack = (status) => {
            if (status.isError()) {
              // ignore reject
              return;
            }
            this.arguments[name] = value;
          };
        };
      };

      this.client.onclipboard = clipboard.onClipboard;
      this.display = this.client.getDisplay();
      const displayElm = this.$refs.display;
      displayElm.appendChild(this.display.getElement());
      displayElm.addEventListener('contextmenu', (e) => {
        e.stopPropagation();
        if (e.preventDefault) {
          e.preventDefault();
        }
        e.returnValue = false;
      });

      const param = serialize(this.query);
      this.client.connect(param);
      window.onunload = () => this.client.disconnect();

      this.mouse = new Guacamole.Mouse(displayElm);
      // Hide software cursor when mouse leaves display
      this.mouse.onmouseout = () => {
        if (!this.display) return;
        this.display.showCursor(false);
      };

      // allows focusing on the display div so that keyboard doesn't always go to session
      displayElm.onclick = () => {
        displayElm.focus();
      };
      displayElm.onfocus = () => {
        displayElm.className = 'focus';
      };
      displayElm.onblur = () => {
        displayElm.className = '';
      };

      this.keyboard = new Guacamole.Keyboard(displayElm);
      this.installKeyboard();
      this.mouse.onmousedown = this.handleMouseState;
      this.mouse.onmouseup = this.handleMouseState;
      this.mouse.onmousemove = this.handleMouseState;
      setTimeout(() => {
        this.resize();
        displayElm.focus();
      }, 1000); // $nextTick wasn't enough
    },
    installKeyboard() {
      this.keyboard.onkeydown = (keysym) => {
        this.client.sendKeyEvent(1, keysym);
      };
      this.keyboard.onkeyup = (keysym) => {
        this.client.sendKeyEvent(0, keysym);
      };
    },
    uninstallKeyboard() {
      this.keyboard.onkeydown = () => {
      };
      this.keyboard.onkeyup = () => {
      };
    },
    // 监听剪贴板变化
    handleCanvasCopyData() {
      const { userAgent } = window.navigator;
      if (userAgent.indexOf('Firefox') === -1) {
        navigator.clipboard.readText()
          .then((text) => {
            clipboard.cache = {
              type: 'text/plain',
              data: text,
            };
            clipboard.setRemoteClipboard(this.client);
            // 在这里处理剪贴板文本
          })
          .catch(() => {
            // console.error('Failed to read clipboard contents: ', err);
          });
      };
    },
  },
  mounted() {
    this.$eventBus.$on('COSBrowserCopyData', (data) => {
      clipboard.cache = {
        type: 'text/plain',
        data,
      };
      clipboard.setRemoteClipboard(this.client);
    });
  },
  beforeDestroy() {
    this.$eventBus.$off('COSBrowserCopyData');
  },
};
</script>

<style scoped lang="scss">
// 远程连接组件
.container {
  height: calc(100vh - 46px);
  width: 100vw;
  // 主要图层
  .el-main{
    padding: 4px;
  }
  // 展示层
  .display {
    overflow: hidden;
    width: 100%;
    height: 100%;
    display: inline-block;
  }
  // 可视canvas层
  .viewport {
    // background-color: coral;
    // border-color: blue;
    // border-width: 1px;
    position: relative;
    width: 100%;
    height: 100%;
    text-align: center;
  }

  .popContainer{
    z-index: 999;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #ffffff4d;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .connectAgain{
    width: 420px;
    height: 100px;
    text-align: center;
    line-height: 100px;
    color: white;
    background: black;
    border-radius: 10px;
    cursor:pointer;
  }
}
</style>
